import { useEffect, useState, useMemo, useRef } from "react";
import {
    Button,
    Card,
    Form,
    FormGroup,
    Input,
    Label,
    UncontrolledTooltip,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

import useFetch from "Hooks/useFetch";
import styles from "Pages/Run/Components/SearchBox/SearchBox.module.css";
import useUserContext from "Hooks/useUserContext";
import useAlertContext from "Hooks/useAlertContext";
import { IColumnFilters } from "Pages/Run/Run";

interface IProps<T> {
    onSetIsLoading: (isLoading: boolean) => void;
    messages: T[];
    onSetMessages: Function;
    parent: string;
    columnFilters?: IColumnFilters;
    onSetTriggerGoToPageOne?: (value: boolean) => void;
    onSetIsRunPageAutoFetching?: (value: boolean) => void;
    showIgnored?: boolean;
    onSetShowIgnored?: () => void;
    onSetUtcNow?: (value: string) => void;
    /** Set by the activity histogram when a month bar is clicked. */
    dateRangeOverride?: { from: string; to: string };
    /** Reports how many rows were loaded, whether the cap cut the result, and whether this was a
     *  "Load all" fetch. */
    onSetSearchTotals?: (totals: {
        total: number;
        truncated: boolean;
        loadAll: boolean;
    }) => void;
    /** Incremented by the parent's "Load all" button. Lifts the cap for the CURRENT search only;
     *  changing the search drops back to the normal cap. */
    loadAllRequest?: number;
    /** The From/Through currently in force, so the parent can mirror it (histogram highlight). */
    onDateRangeChange?: (range: { from: string; to: string }) => void;
}

interface IFormData {
    from: string;
    to: string;
    radioBtn: string;
    previews: boolean | undefined;
    created: boolean | undefined;
    updated: boolean | undefined;
    deleted: boolean | undefined;
    warning: boolean | undefined;
    errored: boolean | undefined;
    actions: boolean | undefined;
}

export default function SearchBox<T>({
    onSetIsLoading,
    messages,
    onSetMessages,
    parent,
    columnFilters,
    onSetTriggerGoToPageOne,
    onSetIsRunPageAutoFetching,
    showIgnored,
    onSetShowIgnored,
    onSetUtcNow,
    dateRangeOverride,
    onSetSearchTotals,
    loadAllRequest = 0,
    onDateRangeChange,
}: IProps<T>) {
    const [formData, setFormData] = useState(makeDefaultFormData());
    const [userContext] = useUserContext();

    const { post } = useFetch();
    const { newAlert } = useAlertContext();

    const isFirstFetch = useRef(true);
    const isFetchInProgress = useRef(false);
    /** A search requested while another fetch was in flight. It used to be silently dropped and
     *  the 3-second poll picked it up; a past range no longer polls, so it must be re-run when the
     *  in-flight fetch completes. */
    const isRefetchPending = useRef(false);
    /** What the last fetch searched for. A change here (dates, action toggles, column filters —
     *  including a histogram bar click) is a new question and gets the loading spinner; a poll
     *  tick asking the same question does not. */
    const lastSearchKey = useRef("");
    /** The search key "Load all" was pressed for. load_all is sent only while the search still
     *  matches it, so a new search silently returns to the normal cap. */
    const loadAllKey = useRef("");
    const lastLoadAllRequest = useRef(0);

    /** A range that ends before today cannot gain rows, so polling it every 3 seconds is pure
     *  load on the DB for an answer that is already final. Only a range that reaches today can
     *  change. */
    const isRangeHistorical = useMemo(() => {
        if (formData.to === "") return false;
        const through = new Date(formData.to);
        if (isNaN(through.getTime())) return false;
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        return through < startOfToday;
    }, [formData.to]);

    /** Controls interval of fetches for message headers in Run. Defaults to POLL_IDLE_MS but
     * quickens to POLL_RUNNING_MS while an integration is currently running.
     *
     * THE RUNNING INTERVAL IS DELIBERATELY 1 s — do not raise it to save database load.
     * That trade was measured and rejected on 2026-09-03. Each poll compiles to a full-entity
     * message_header SELECT, and for any user without Review viewall/rerunall the server
     * force-adds an `initiator:` filter (Controllers/Run/RunHelpers.cs:41-44), so the poll was
     * briefly prod's top CPU consumer. Backing off 1 s -> 2 s was worth only ~0.50 average active
     * sessions (~9% of load), and the real causes were elsewhere: shuttle_register had no indexes
     * at all, so every shuttle heartbeat full-scanned 20k rows (44% of load, dimension-ed6ep), and
     * the initiator predicate had no index either (dimension-y4vg). Indexing both dropped total DB
     * load from 4.90 to 1.23 AAS on the 2-vCPU instance, which is real headroom. So the 1 s
     * responsiveness a user watching a live integration gets is no longer paid for in CPU.
     * See dimension-1lm7 for the ALB measurements (/api/run at 3.82 req/s, mean 747 ms — the
     * bead's original ~114 req/s estimate was wrong by ~30x).
     */
    const POLL_RUNNING_MS = 1000;
    const POLL_IDLE_MS = 3000;

    const fetchInterval = useMemo(() => {
        if (parent === "Run")
            return messages.some((m: any) => m.status === "Started")
                ? POLL_RUNNING_MS
                : POLL_IDLE_MS;
    }, [messages, parent]);

    // Fetch to backend API to search messages and message headers table on
    // page load and any time the inputs change
    useEffect(() => {
        let ignore = false;

        const searchKey = JSON.stringify({ formData, columnFilters });

        if (loadAllRequest !== lastLoadAllRequest.current) {
            lastLoadAllRequest.current = loadAllRequest;
            loadAllKey.current = searchKey;
        }
        const load_all = loadAllKey.current === searchKey;

        const isNewSearch = searchKey + load_all !== lastSearchKey.current;
        lastSearchKey.current = searchKey + load_all;

        // A new question shows the spinner while the answer is fetched, so a clicked month gives
        // visible feedback instead of a table that looks stale until it suddenly changes.
        if (isNewSearch) {
            onSetIsLoading(true);
        }

        searchMessages();

        // Re-fetch on an interval only inside Run, and only when the range reaches today — a
        // past month is final and re-asking changes nothing.
        //
        // A hidden tab issues no request at all. Browser timer throttling already slows a hidden
        // tab's timer (Chrome clamps to >=1 s, then to ~1/min after 5 minutes hidden), so this
        // recovers the first few minutes of each hidden period rather than a large share of the
        // load -- correctness hygiene, not the lever. Because a hidden tab therefore stops
        // updating, becoming visible again fetches immediately instead of waiting for the next
        // tick, which under intensive throttling could be up to a minute away.
        const interval =
            parent === "Run" && !isRangeHistorical
                ? setInterval(() => {
                      if (document.hidden) return;
                      searchMessages();
                  }, fetchInterval)
                : undefined;

        const onVisibilityChange = () => {
            if (!document.hidden) searchMessages();
        };
        if (parent === "Run") {
            document.addEventListener("visibilitychange", onVisibilityChange);
        }

        // Cancel that interval after fixed time
        // const oneHourInMs = 1 * 60 * 60 * 1000;
        if (parent === "Run" && onSetIsRunPageAutoFetching) {
            const thirtyMinInMs = 30 * 60 * 1000;
            setTimeout(() => {
                clearInterval(interval);
                onSetIsRunPageAutoFetching(false);
            }, thirtyMinInMs);
        }

        async function searchMessages() {
            /** Only show loading spinner the first time */
            if (isFirstFetch.current) {
                isFirstFetch.current = false;
                onSetIsLoading(true);
            }

            if (isFetchInProgress.current) {
                isRefetchPending.current = true;
                return;
            }
            isFetchInProgress.current = true;

            try {
                const filters: string[] = makeFiltersFromFormData();

                /** Add column filters from Run.tsx or RunSearch.tsx, will end up like { name: "test", initiator: "someone" } */
                const columnFiltersToAdd: string[] =
                    makeFiltersFromColumnFilters(columnFilters);
                filters.push(...columnFiltersToAdd);

                const start_date = new Date(formData.from);
                const end_date = new Date(formData.to);

                const body = {
                    filters,
                    start_date,
                    end_date,
                    company_id: userContext.companyId,
                    load_all,
                };

                let url =
                    parent === "RunSearch"
                        ? "/api/run/message_search"
                        : "/api/run";
                const response = await post(url, body);

                const data = await response.json();
                console.log("Message search response: ", data);

                if (ignore) return;

                if (response.ok) {
                    onSetMessages(data.messages);
                    if (onSetUtcNow) {
                        onSetUtcNow(data.utcNow);
                    }
                    if (onSetSearchTotals) {
                        onSetSearchTotals({
                            total: data.total_matching ?? data.messages.length,
                            truncated: data.truncated ?? false,
                            loadAll: data.load_all ?? false,
                        });
                    }
                } else {
                    newAlert({
                        message: "Error fetching messages",
                    });
                }
            } finally {
                isFetchInProgress.current = false;
                onSetIsLoading(false);
                if (isRefetchPending.current && !ignore) {
                    isRefetchPending.current = false;
                    searchMessages();
                }
            }
        }

        function makeFiltersFromFormData(): string[] {
            const filters = Object.entries(formData)
                .filter(
                    ([key, value]) =>
                        value !== "" &&
                        value !== undefined &&
                        key !== "from" &&
                        key !== "to"
                )
                .map(([key, value]) => {
                    if (key === "radioBtn") {
                        return value;
                    } else {
                        if (value === true) {
                            return key;
                        } else {
                            return `!${key}`;
                        }
                    }
                });

            return filters;
        }

        function makeFiltersFromColumnFilters(
            columnFilters: IColumnFilters | undefined
        ) {
            if (!columnFilters) return [];

            const filters = Object.entries(columnFilters)
                .filter(([key, value]) => value !== "")
                .map(([key, value]) => {
                    return `${key}:${value}`;
                });

            return filters;
        }

        return () => {
            ignore = true;
            if (interval) clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [
        columnFilters,
        fetchInterval,
        formData,
        isRangeHistorical,
        loadAllRequest,
        newAlert,
        onSetIsLoading,
        onSetIsRunPageAutoFetching,
        onSetMessages,
        onSetSearchTotals,
        onSetUtcNow,
        parent,
        post,
        userContext.companyId,
    ]);

    // A month clicked on the activity histogram is just a date range, so it lands in the same
    // formData the pickers write to — one source of truth for the range, and the pickers visibly
    // move to show where you are.
    useEffect(() => {
        if (!dateRangeOverride) return;
        setFormData((prev: any) => ({
            ...prev,
            from: dateRangeOverride.from,
            to: dateRangeOverride.to,
        }));
    }, [dateRangeOverride]);

    useEffect(() => {
        if (onDateRangeChange) {
            onDateRangeChange({ from: formData.from, to: formData.to });
        }
    }, [onDateRangeChange, formData.from, formData.to]);

    // Update triggerGoToPageOne state in parent to trigger a useEffect in ReactTable.tsx
    // that will set the page to 1 any time filters change
    useEffect(() => {
        if (onSetTriggerGoToPageOne) {
            onSetTriggerGoToPageOne(true);
        }
    }, [onSetTriggerGoToPageOne, formData]);

    function makeDefaultFormData() {
        const today = new Date();
        const twoDaysAgo = new Date(today);
        twoDaysAgo.setDate(today.getDate() - 2);
        const defaultFormData: IFormData = {
            radioBtn: "",
            from:
                parent === "RunSearch"
                    ? twoDaysAgo.toISOString().split("T")[0]
                    : "",
            to: parent === "RunSearch" ? today.toISOString().split("T")[0] : "",
            created: undefined,
            updated: undefined,
            deleted: undefined,
            warning: undefined,
            errored: undefined,
            actions: undefined,
            previews: undefined,
        };
        return defaultFormData;
    }

    function handleFormChange(e: React.ChangeEvent<HTMLInputElement>) {
        const name = e.target.name;
        let value: boolean | string = e.target.value;

        if (e.target.type === "checkbox") {
            value = e.target.checked;
        }

        setFormData((formData: any) => ({ ...formData, [name]: value }));
    }

    function handleActionButtonClick(e: React.MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        const action = e.currentTarget.name as keyof IFormData;

        const previous = formData[action];
        const newValue =
            previous === undefined
                ? true
                : previous === true
                ? false
                : undefined;
        setFormData((prev: any) => ({
            ...prev,
            [action]: newValue,
        }));
    }

    function resetForm(e: React.FormEvent<HTMLButtonElement>): void {
        e.preventDefault();
        setFormData((prev: any) => ({
            ...makeDefaultFormData(),
        }));
    }

    /** The range as the stepper sees it — inclusive dates, and a whole calendar month steps by
     *  month so Aug 1–31 goes to Jul 1–31, not Jul 2–Aug 1. Undefined when unusable. */
    function describePeriod(): { label: string; wholeMonth: boolean; days: number } | undefined {
        const from = new Date(formData.from);
        const to = new Date(formData.to);
        if (isNaN(from.getTime()) || isNaN(to.getTime()) || from > to) return undefined;
        const days = Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
        const lastOfMonth = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth() + 1, 0)).getUTCDate();
        const wholeMonth =
            from.getUTCFullYear() === to.getUTCFullYear() &&
            from.getUTCMonth() === to.getUTCMonth() &&
            from.getUTCDate() === 1 &&
            to.getUTCDate() === lastOfMonth;
        const monthYear = { month: "short", year: "numeric", timeZone: "UTC" } as const;
        let label: string;
        if (wholeMonth) {
            label = from.toLocaleDateString(undefined, monthYear);
        } else if (days === 1) {
            label = from.toLocaleDateString(undefined, { ...monthYear, day: "numeric" });
        } else {
            label = `${days} days`;
        }
        return { label, wholeMonth, days };
    }

    /** On Chevron click, moves the range one period back or forward */
    function handleChevronClick(e: React.MouseEvent, change: string): void {
        e.preventDefault();

        const period = describePeriod();
        if (!period) return;
        const from = new Date(formData.from);
        const to = new Date(formData.to);
        const iso = (d: Date) => d.toISOString().split("T")[0];
        const step = change === "<" ? -1 : 1;

        let newFrom: Date;
        let newTo: Date;
        if (period.wholeMonth) {
            newFrom = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + step, 1));
            newTo = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + step + 1, 0));
        } else {
            const shift = step * period.days * 86400000;
            newFrom = new Date(from.getTime() + shift);
            newTo = new Date(to.getTime() + shift);
        }

        setFormData({
            ...formData,
            from: iso(newFrom),
            to: iso(newTo),
        });
    }

    /** Disable the last/next time period buttons if inputs empty or in wrong order */
    /** A single day (from === to) is a valid one-day period and steps by a day. */
    function isDisabled(direction: string): boolean {
        return describePeriod() === undefined;
    }

    /** Calculated based on parent prop- which component it is used in */
    const inputsByParent =
        parent === "RunSearch" ? (
            <FormGroup tag="fieldset" className={styles.fieldset}>
                <legend className={styles.legend}>View only:</legend>
                {[
                    "info",
                    "created",
                    "updated",
                    "deleted",
                    "warning",
                    "errored",
                    "actions",
                    "previews",
                ].map((v) => (
                    <FormGroup key={v} check inline>
                        <Input
                            id={v}
                            name="radioBtn"
                            type="radio"
                            value={v}
                            checked={formData.radioBtn === v}
                            onChange={(e) => handleFormChange(e)}
                        />
                        <Label check htmlFor={v}>
                            {v[0].toUpperCase() + v.slice(1)}
                        </Label>
                    </FormGroup>
                ))}
            </FormGroup>
        ) : (
            [
                "created",
                "updated",
                "deleted",
                "warning",
                "errored",
                "actions",
                "previews",
            ].map((action) => (
                <label
                    key={action}
                    htmlFor={action}
                    className={styles.inputLabelPair}
                >
                    <Button
                        className={styles.actionButton}
                        type="button"
                        id={action}
                        name={action}
                        onClick={(e) => handleActionButtonClick(e)}
                        style={{
                            backgroundColor: getActionButtonColor(
                                action as keyof IFormData
                            ),
                        }}
                    />
                    <UncontrolledTooltip target={action}>
                        {getToolTipText(action as keyof IFormData)}
                    </UncontrolledTooltip>
                    {action[0].toUpperCase() + action.slice(1)}
                </label>
            ))
        );

    function getActionButtonColor(action: keyof IFormData) {
        const value = formData[action];
        return value === undefined
            ? "var(--bs-secondary)"
            : value
            ? "var(--bs-success)"
            : "var(--bs-danger)";
    }

    function getToolTipText(action: keyof IFormData) {
        const value = formData[action];
        return value === undefined
            ? "No effect - click to require " + action
            : value
            ? "Required - click to exclude " + action
            : "Exluded - click to neither require nor exclude " + action;
    }

    return (
        <Card>
            <Form className={styles.formBox}>
                <div className={styles.boxRowOne}>
                    {inputsByParent}
                    {parent === "Run" && (
                        <Button
                            className={styles.button}
                            onClick={onSetShowIgnored}
                        >
                            {showIgnored ? "Hide Ignored" : "Show Ignored"}
                        </Button>
                    )}
                    <Button
                        className={styles.button}
                        color="primary"
                        onClick={resetForm}
                    >
                        Reset
                    </Button>
                </div>

                {/* Date Inputs */}
                <div className={styles.dateInputsBox}>
                    <FormGroup className={styles.dateInputs}>
                        <Label check>From: </Label>
                        <Input
                            className="ms-1"
                            name="from"
                            type="date"
                            onChange={(e) => handleFormChange(e)}
                            value={formData.from}
                            max={
                                formData.to !== undefined
                                    ? formData.to
                                    : undefined
                            }
                            style={{ width: "100%", maxWidth: "120px" }}
                        />
                    </FormGroup>
                    <FormGroup className={styles.dateInputs}>
                        <Label check>Through: </Label>
                        <Input
                            className="ms-1"
                            name="to"
                            type="date"
                            onChange={(e) => handleFormChange(e)}
                            value={formData.to}
                            min={
                                formData.from !== undefined
                                    ? formData.from
                                    : undefined
                            }
                            style={{ width: "100%", maxWidth: "120px" }}
                        />
                    </FormGroup>
                    <FormGroup className={styles.chevronBtnsBox}>
                        <Button
                            id="chevronLeft"
                            color="secondary"
                            className={styles.chevronBtns}
                            onClick={(e) => handleChevronClick(e, "<")}
                            disabled={isDisabled("<")}
                        >
                            <FontAwesomeIcon
                                className={styles.chevron}
                                icon={faChevronLeft}
                            />
                        </Button>
                        <UncontrolledTooltip target="chevronLeft">
                            Previous {describePeriod()?.wholeMonth ? "month" : "period"}
                        </UncontrolledTooltip>
                        <span className={styles.periodLabel}>{describePeriod()?.label ?? "—"}</span>
                        <Button
                            id="chevronRight"
                            color="secondary"
                            className={styles.chevronBtns}
                            onClick={(e) => handleChevronClick(e, ">")}
                            disabled={isDisabled(">")}
                        >
                            <FontAwesomeIcon
                                className={styles.chevron}
                                icon={faChevronRight}
                            />
                        </Button>
                        <UncontrolledTooltip target="chevronRight">
                            Next {describePeriod()?.wholeMonth ? "month" : "period"}
                        </UncontrolledTooltip>
                    </FormGroup>
                </div>
            </Form>
        </Card>
    );
}
